import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from 'src/app/services/user.service';
import { AuthService } from 'src/app/services/auth.service';
import { SharedService } from 'src/app/services/sharedService';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { authErrorKeys } from 'src/app/util/auth-error.util';

/**
 * ADMIN screen: invite someone to create a local account.
 *
 * This is the only way a local account comes into being -- there is no
 * self-registration endpoint. The invitee sets their own password and enrols a
 * second factor from the emailed link, so this form never handles a password and
 * an administrator never learns one.
 *
 * Tax offices and control types are deliberately NOT on this form. They are
 * multi-selects on the existing edit screen, and duplicating them here would
 * double the maintenance for fields that can be set the moment the invitation is
 * accepted. The form stays to what an invitation actually needs.
 */
@Component({
    selector: 'app-user-invite',
    templateUrl: './user-invite.component.html',
    standalone: false
})
export class UserInviteComponent implements OnInit {

  /** Same set, in the same order, as the switches on user-add-edit. */
  readonly roleKeys = [
    'ADMIN', 'EDITOR', 'VIEWER', 'REGIONAL_OFFICER', 'DIRECTOR_GENERAL',
    'DIRECTOR_APPROVER', 'INSPECTOR', 'CHEF', 'COORDINATOR', 'CONTROLLER'
  ];

  username = '';
  name = '';
  surname = '';
  roles: string[] = [];

  busy = false;
  localEnabled = true;
  errorKeys: string[] = [];

  constructor(private userService: UserService,
              private auth: AuthService,
              private sharedService: SharedService,
              private toastr: ToastrService,
              private translate: TranslateService,
              private router: Router) {}

  ngOnInit() {
    this.sharedService.loadMenuItems('admin');
    // Invitations only mean anything where local authentication is switched on.
    // Rather than let the form fail on submit, say so up front.
    this.auth.providers$().subscribe({
      next: (p) => { this.localEnabled = !!p.local; },
      error: () => { this.localEnabled = false; }
    });
  }

  toggleRole(role: string, checked: boolean) {
    const at = this.roles.indexOf(role);
    if (checked && at === -1) this.roles.push(role);
    if (!checked && at !== -1) this.roles.splice(at, 1);
  }

  hasRole(role: string): boolean {
    return this.roles.indexOf(role) !== -1;
  }

  submit() {
    if (this.busy || !this.username) return;
    this.busy = true;
    this.errorKeys = [];
    this.userService.invite({
      username: this.username.trim(),
      name: this.name,
      surname: this.surname,
      roles: this.roles
    }).subscribe({
      next: () => {
        this.busy = false;
        this.toastr.success(this.translate.instant('AUTH.INVITE_SENT', { username: this.username }));
        this.router.navigateByUrl('/user');
      },
      error: (err) => {
        this.busy = false;
        this.errorKeys = authErrorKeys(err);
      }
    });
  }

  cancel() {
    this.router.navigateByUrl('/user');
  }
}
